import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { Label } from 'components/ui/label';
import { Checkbox } from 'components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { formatDateWithOrdinal, titleCase, upperCase } from 'lib/utils';
import { getLastLhc, updateDeliveryDates, LhcData, DeliveryDateUpdateResponse, updateArrivalDates } from '../api/shipments';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { Alert, AlertDescription } from "components/ui/alert";
import { AlertCircle } from "lucide-react";

interface LhcModalProps {
  isOpen: boolean;
  onClose: () => void;
  cnNumber: string;
  onSuccess?: () => void;
  onRefreshAlerts?: () => void;
}

export const LhcModal: React.FC<LhcModalProps> = ({ isOpen, onClose, cnNumber, onSuccess, onRefreshAlerts }) => {
  const [lhcData, setLhcData] = useState<LhcData | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedCns, setSelectedCns] = useState<Set<string>>(new Set());
  const [arrivalDate, setArrivalDate] = useState<Date | undefined>(undefined);
  const [arrivalDateInput, setArrivalDateInput] = useState<string>('');
  const [updatingArrival, setUpdatingArrival] = useState<boolean>(false);
  const [deliveryDate, setDeliveryDate] = useState<Date | undefined>(undefined);
  const [deliveryDateInput, setDeliveryDateInput] = useState('');
  const [updating, setUpdating] = useState(false);
  const [arrivalError, setArrivalError] = useState<string | null>(null);
  const [deliveryError, setDeliveryError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && cnNumber) {
      fetchLhcData();
    }
  }, [isOpen, cnNumber]);

  useEffect(() => {
    // Reset state when modal opens/closes
    if (!isOpen) {
      setLhcData(null);
      setSelectedCns(new Set());
      setDeliveryDate(undefined);
      setDeliveryDateInput('');
    }
  }, [isOpen]);

  const fetchLhcData = async () => {
    setLoading(true);
    try {
      const data = await getLastLhc(cnNumber);
      setLhcData(data);
    } catch (error) {
      console.error('Error fetching LHC data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    if (!lhcData) return;
    
    if (selectedCns.size === lhcData.cns.length) {
      // Deselect all
      setSelectedCns(new Set());
    } else {
      // Select all
      setSelectedCns(new Set(lhcData.cns.map(cn => cn.cn_number)));
    }
  };

  const handleCnSelect = (cnNumber: string, checked: boolean) => {
    const newSelected = new Set(selectedCns);
    if (checked) {
      newSelected.add(cnNumber);
    } else {
      newSelected.delete(cnNumber);
    }
    setSelectedCns(newSelected);
  };

  const handleDateInputChange = (value: string) => {
    setDeliveryDateInput(value);
    if (value) {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        setDeliveryDate(date);
      } else {
        setDeliveryDate(undefined);
      }
    } else {
      setDeliveryDate(undefined);
    }
  };

  const handleArrivalUpdate = async () => {
    if (!arrivalDate || selectedCns.size === 0) return;

    setUpdatingArrival(true);
    setArrivalError(null);
    setDeliveryError(null);

    try {
      const isoDate = format(arrivalDate, "yyyy-MM-dd'T'00:00:00'Z'");
      const response = await updateArrivalDates(isoDate, Array.from(selectedCns));

      if (response.failed === 0) {
        toast.success('Arrival Dates Updated Successfully');
        await fetchLhcData();
        setSelectedCns(new Set());
        setArrivalDate(undefined);
        setArrivalDateInput('');
      } else {
        const msg = response.results.find((r: any) => !r.success)?.message;
        setArrivalError(msg || 'Failed to update arrival dates');
      }
    } catch (error) {
      toast.error('Failed to update arrival dates');
    } finally {
      setUpdatingArrival(false);
    }
  };

  const handleUpdate = async () => {
    if (!deliveryDate || selectedCns.size === 0) return;

    setUpdating(true);
    setArrivalError(null);
    setDeliveryError(null);
    try {
      const isoDate = format(deliveryDate, "yyyy-MM-dd'T'00:00:00'Z'");
      const response = await updateDeliveryDates(isoDate, Array.from(selectedCns));

      // 🧠 Check if backend sent a specific date validation error
      const failedMsg = response.results.find((r: any) => !r.success)?.message;
      if (failedMsg) {
        const formattedMsg = failedMsg.replace(
          /\d{4}-\d{2}-\d{2}/g,
          (match) => formatDateWithOrdinal(match)
        );

        setDeliveryError(formattedMsg);
        return;
      }

      if (response.failed === 0) {
        toast.success('Delivery Dates Updated Successfully', {
          style: {
            background: '#f0fdf4',
            color: '#15803d',
            border: '1px solid #bbf7d0'
          }
        });
        onSuccess?.(); 
        onClose(); // Close the modal
      }
    } catch (error) {
      console.error('Error updating delivery dates:', error);
      toast.error('Failed to update delivery dates', {
        style: {
          background: '#fef2f2',
          color: '#dc2626',
          border: '1px solid #fecaca'
        }
      });
    } finally {
      setUpdating(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
      <Dialog open={isOpen} onOpenChange={(open) => {
          if (!open) {
            setArrivalError(null);
            setDeliveryError(null);
            onClose();
        }}}>
        <DialogContent className="w-[80vw] !max-w-none max-h-[90vh] overflow-y-auto">
        <VisuallyHidden>
          <DialogTitle>LHC Details</DialogTitle>
        </VisuallyHidden>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-sm text-gray-500">Loading LHC data...</div>
          </div>
        ) : lhcData ? (
          <div className="space-y-6">
            {/* LHC Details Card */}
            {lhcData.lhc_number && (
              <Card>
                <CardContent className="pt-5">
                  <div className="grid grid-cols-5 gap-4 text-sm">
                    <div>
                      <Label className="text-xs font-medium text-gray-500">LHC Number</Label>
                      <div className="font-medium text-sm">{lhcData.lhc_number}</div>
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-gray-500">LHC Date</Label>
                      <div className="font-medium text-sm">{formatDate(lhcData.lhc_date)}</div>
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-gray-500">Vehicle Number</Label>
                      <div className="font-medium text-sm">{lhcData.vehicle_no}</div>
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-gray-500">Origin</Label>
                      <div className="font-medium text-sm">{titleCase(lhcData.lhc_origin)}</div>
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-gray-500">Destination</Label>
                      <div className="font-medium text-sm">{titleCase(lhcData.lhc_destination)}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            {/* CNs Table */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-md">Consignment Notes ({lhcData.cns.length})</CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSelectAll}
                  className="text-xs"
                >
                  {selectedCns.size === lhcData.cns.length ? 'Deselect All' : 'Select All'}
                </Button>
              </CardHeader>
              <CardContent>
                <div className="border rounded-md">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">Select</TableHead>
                        <TableHead>CN Number</TableHead>
                        <TableHead>CN Date</TableHead>
                        <TableHead>Branch</TableHead>
                        <TableHead>Consignor</TableHead>
                        <TableHead>Consignee</TableHead>
                        <TableHead>Origin</TableHead>
                        <TableHead>Destination</TableHead>
                        <TableHead>EDD</TableHead>
                        <TableHead>Distance</TableHead>
                        {lhcData.enable_arrival_date && (
                          <TableHead>Arrival Date</TableHead>
                        )}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {lhcData.cns.map((cn) => (
                        <TableRow 
                          key={cn.cn_number}
                          className={selectedCns.has(cn.cn_number) ? 'bg-blue-50 border-blue-200' : ''}
                        >
                          <TableCell>
                            <Checkbox
                              className='border-gray-400'
                              checked={selectedCns.has(cn.cn_number)}
                              onCheckedChange={(checked) => 
                                handleCnSelect(cn.cn_number, checked as boolean)
                              }
                            />
                          </TableCell>
                          <TableCell className="font-medium text-xs">{cn.cn_number}</TableCell>
                          <TableCell className="text-xs">{formatDate(cn.cn_date)}</TableCell>
                          <TableCell className="text-xs">{titleCase(cn.branch_name)}</TableCell>
                          <TableCell className="text-xs max-w-[120px] truncate" title={cn.consignor}>
                            {upperCase(cn.consignor)}
                          </TableCell>
                          <TableCell className="text-xs max-w-[120px] truncate" title={cn.consignee}>
                            {upperCase(cn.consignee)}
                          </TableCell>
                          <TableCell className="text-xs">{titleCase(cn.origin)}</TableCell>
                          <TableCell className="text-xs">{titleCase(cn.destination)}</TableCell>
                          <TableCell className="text-xs">{cn.edd ? formatDate(cn.edd) : '-'}</TableCell>
                          <TableCell className="text-xs">{cn.remaining_km ? cn.remaining_km + ' km' : '-'}</TableCell>
                          {lhcData.enable_arrival_date && (
                            <TableCell className="text-xs">
                              {cn.cto_arrival_date ? (
                                <span className="text-green-700 font-medium">
                                  {formatDate(cn.cto_arrival_date)}
                                </span>
                              ) : (
                                <span className="text-red-500">Pending</span>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            {/* Arrival Date Update Section */}
            {lhcData.enable_arrival_date && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-md">Update Arrival Date</CardTitle>
                </CardHeader>

                <CardContent className="space-y-4">
                  <input
                    type="date"
                    value={arrivalDateInput}
                    onChange={(e) => {
                      setArrivalDateInput(e.target.value);
                      const date = new Date(e.target.value);
                      setArrivalDate(!isNaN(date.getTime()) ? date : undefined);
                    }}
                    max={new Date().toISOString().split('T')[0]}
                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg"
                  />

                  {arrivalError && (
                    <Alert variant="destructive" className="border-red-300 bg-red-50 text-red-800">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{arrivalError}</AlertDescription>
                    </Alert>
                  )}

                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-600">
                      {selectedCns.size} CN(s) selected
                    </div>

                    <Button
                      onClick={handleArrivalUpdate}
                      disabled={selectedCns.size === 0 || !arrivalDate || updatingArrival}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      {updatingArrival ? 'Updating...' : 'Update Arrival Date'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Delivery Date Update Section */}
            <Card>
              <CardHeader>
                <CardTitle className="text-md">Update Delivery Date</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-2">
                  <div className="flex-1">
                  
                    <input
                      type="date"
                      id="delivery-date"
                      name="delivery-date"
                      value={deliveryDateInput}
                      onChange={(e) => handleDateInputChange(e.target.value)}
                      required
                      max={new Date().toISOString().split('T')[0]}
                      className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-left"
                    />
                  </div>
                </div>

                {deliveryError && (
                  <Alert variant="destructive" className="border-red-300 bg-red-50 text-red-800">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{deliveryError}</AlertDescription>
                  </Alert>
                )}

                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    {selectedCns.size} CN(s) selected
                  </div>
                  <Button
                    onClick={handleUpdate}
                    disabled={selectedCns.size === 0 || !deliveryDate || updating}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {updating ? 'Updating...' : 'Update Delivery Date'}
                  </Button>
                </div>
              </CardContent>
            </Card>

          </div>
        ) : (
          <div className="flex items-center justify-center py-8">
            <div className="text-sm text-gray-500">Failed to load LHC data</div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
